import { useEffect, useState } from 'preact/hooks';
import {
  loadManufacturers,
  manufacturers,
  runSearch,
  searchParams,
  showMrc,
  expandGroups,
  lastFilters,
} from '@/store/catalog';
import { isMobile } from '@/lib/viewport';
import { TIRE_DIAMETERS, TIRE_PROFILES, TIRE_WIDTHS } from './filter-config.js';
import css from './SearchForm.module.css';

const INITIAL = {
  width: '',
  profile: '',
  diameter: '',
  season: '',
  category_type: 'tires',
  width2: '',
  profile2: '',
  diameter2: '',
  tire_type: '',
  manufacturer: '',
  studded: false,
  run_flat: false,
  is_at: false,
  is_mt: false,
  name: '',
  hide_opt: false,
  enable_second_size: false,
};

export function SearchForm() {
  const [form, setForm] = useState(INITIAL);
  // На мобиле панель параметров свёрнута по умолчанию — экономим экран, как в нативном приложении.
  const [open, setOpen] = useState(!isMobile.value);

  useEffect(() => {
    if (manufacturers.value.length === 0) loadManufacturers();
  }, []);

  // Автозапуск при первом монтировании.
  useEffect(() => {
    runSearch();
  }, []);

  function set(key, value) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  function buildFilters() {
    const f = {};
    const numericFields = new Set(['width', 'profile', 'diameter', 'width2', 'profile2', 'diameter2']);
    for (const [key, value] of Object.entries(form)) {
      if (!value || key === 'hide_opt' || key === 'enable_second_size') continue;
      if (['studded', 'run_flat', 'is_at', 'is_mt'].includes(key)) {
        f[key] = true;
        continue;
      }
      if (key === 'tire_type' && value === 'light_truck') {
        f['is_light_truck'] = true;
        continue;
      }
      if (key === 'tire_type' && value === 'passenger') {
        f['is_passenger'] = true;
        continue;
      }
      if (!form.enable_second_size && (key === 'width2' || key === 'profile2' || key === 'diameter2')) {
        continue;
      }
      if (numericFields.has(key)) f[key] = value;
      else f[key] = value;
    }
    return f;
  }

  function handleSubmit(e) {
    e?.preventDefault?.();
    showMrc.value = !!form.hide_opt;
    searchParams.value = { ...searchParams.value, page: 1 };
    lastFilters.value = buildFilters();
    runSearch();
    if (isMobile.value) setOpen(false); // на мобиле прячем фильтры после поиска — показываем результаты
  }

  function handleReset() {
    setForm(INITIAL);
    showMrc.value = false;
    expandGroups.value = true;
    lastFilters.value = {};
    runSearch();
  }

  function handleMrcToggle(e) {
    set('hide_opt', e.target.checked);
    showMrc.value = e.target.checked;
  }

  const collapsed = isMobile.value && !open;

  return (
    <section class={`panel ${css.panel}`}>
      {isMobile.value && (
        <button
          type="button"
          class={css.collapseHeader}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          <span class={css.collapseTitle}>
            <i class="fa-solid fa-sliders" aria-hidden="true" /> Параметры подбора
          </span>
          <i class={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" />
        </button>
      )}

      <form class={css.form} onSubmit={handleSubmit} onReset={handleReset}>
        <div class={`${css.body} ${collapsed ? css.collapsedBody : ''}`}>
          <div class={css.fields}>
            <Field cap="Ширина" size>
              <NumSelect value={form.width} onChange={(v) => set('width', v)} options={TIRE_WIDTHS} />
            </Field>
            <Field cap="Профиль" size>
              <NumSelect value={form.profile} onChange={(v) => set('profile', v)} options={TIRE_PROFILES} />
            </Field>
            <Field cap="Диаметр" size>
              <NumSelect value={form.diameter} onChange={(v) => set('diameter', v)} options={TIRE_DIAMETERS} />
            </Field>
            <span class={css.break} />

            <Field cap="Сезон">
              <select value={form.season} onChange={(e) => set('season', e.target.value)}>
                <option value="">Любой</option>
                <option value="summer">Летние</option>
                <option value="winter">Зимние</option>
                <option value="all-season">Всесезонные</option>
              </select>
            </Field>
            <Field cap="Категория">
              <select value={form.category_type} onChange={(e) => set('category_type', e.target.value)}>
                <option value="tires">Автошины</option>
                <option value="truck">Грузовые шины</option>
                <option value="moto">Мото/квадро шины</option>
                <option value="agro">Спец/сельхоз шины</option>
              </select>
            </Field>
            <span class={css.break} />

            <label class={css.checkWide}>
              <input
                type="checkbox"
                checked={form.enable_second_size}
                onChange={(e) => set('enable_second_size', e.target.checked)}
              />
              <span>2-й размер для спарки</span>
            </label>

            {form.enable_second_size && (
              <>
                <Field cap="Ширина 2" size>
                  <NumSelect value={form.width2} onChange={(v) => set('width2', v)} options={TIRE_WIDTHS} />
                </Field>
                <Field cap="Профиль 2" size>
                  <NumSelect value={form.profile2} onChange={(v) => set('profile2', v)} options={TIRE_PROFILES} />
                </Field>
                <Field cap="Диаметр 2" size>
                  <NumSelect value={form.diameter2} onChange={(v) => set('diameter2', v)} options={TIRE_DIAMETERS} />
                </Field>
                <span class={css.break} />
              </>
            )}

            <Field cap="Тип шины">
              <select value={form.tire_type} onChange={(e) => set('tire_type', e.target.value)}>
                <option value="">Все</option>
                <option value="passenger">Легковые</option>
                <option value="light_truck">Легкогрузовые</option>
              </select>
            </Field>
            <Field cap="Производитель">
              <select value={form.manufacturer} onChange={(e) => set('manufacturer', e.target.value)}>
                <option value="">Все</option>
                {manufacturers.value.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div class={css.chips}>
            <Check label="ШИП" checked={form.studded} onChange={(c) => set('studded', c)} />
            <Check label="RF" checked={form.run_flat} onChange={(c) => set('run_flat', c)} />
            <Check label="A/T" checked={form.is_at} onChange={(c) => set('is_at', c)} />
            <Check label="M/T" checked={form.is_mt} onChange={(c) => set('is_mt', c)} />
          </div>

          <Field cap="Поиск по названию" full>
            <input
              placeholder="Например: Maxtrek или MOZZO"
              value={form.name}
              onInput={(e) => set('name', e.target.value)}
            />
          </Field>

          <div class={css.toggles}>
            <Check
              label="Развернуть группы"
              checked={expandGroups.value}
              onChange={(c) => {
                expandGroups.value = c;
              }}
            />
            <label class={css.toggleSwitch}>
              <input type="checkbox" checked={form.hide_opt} onChange={handleMrcToggle} />
              <span class={css.toggleSlider} />
              <span>Показывать МРЦ</span>
            </label>
          </div>
        </div>

        <div class={css.actions}>
          <button type="reset" class="btn btn-secondary">
            Очистить
          </button>
          <button type="submit" class={`btn btn-primary ${css.submit}`}>
            <i class="fa-solid fa-magnifying-glass" aria-hidden="true" /> Найти
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({ cap, children, size, full }) {
  return (
    <label class={`${css.field} ${size ? css.size : ''} ${full ? css.full : ''}`}>
      <span class={css.cap}>{cap}</span>
      {children}
    </label>
  );
}

function NumSelect({ value, options, onChange, disabled }) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onWheel={(e) => {
        if (disabled) return;
        e.preventDefault();
        const select = e.currentTarget;
        const dir = e.deltaY > 0 ? 1 : -1;
        const nextIndex = Math.max(0, Math.min(select.selectedIndex + dir, select.options.length - 1));
        select.selectedIndex = nextIndex;
        onChange(select.value);
      }}
    >
      <option value="">Все</option>
      {options.map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <label class={css.check}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
